import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import QRCode from "qrcode";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Print-side: ALLE QR-koder til et event på én gang.
 *
 * Hver QR får sin egen 150x150 mm side (samme layout som enkelt-QR i ResourceMap).
 * - Loader alle resource_points + maxicontainers med qr_ref
 * - Sorterer numerisk efter qr_ref (HE-001, HE-002, ...)
 * - Filtre: vis kun ressourceøer/-spande (skraldespande kan vælges fra)
 * - "Print alle" → browserens print-dialog (gem som PDF eller send til printer)
 */
export default function PrintAllQR({ user, selectedEvent }) {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const eventId = selectedEvent?.id || new URLSearchParams(window.location.search).get("event_id");
  const eventName = selectedEvent?.name || "";

  const [points, setPoints] = useState([]);
  const [maxicontainers, setMaxicontainers] = useState([]);
  const [qrMap, setQrMap] = useState({}); // { qr_ref: dataUrl }
  const [loading, setLoading] = useState(true);
  const [includeBins, setIncludeBins] = useState(false);
  const [includeIslands, setIncludeIslands] = useState(true);
  const [includeMaxis, setIncludeMaxis] = useState(true);
  const [selectedRefs, setSelectedRefs] = useState(() => new Set());  // qr_refs valgt til print

  // Hent data
  useEffect(() => {
    if (!eventId) {
      setLoading(false);
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/resource-points?event_id=${eventId}`, { headers }),
      axios.get(`${API}/maxicontainers?event_id=${eventId}`, { headers }).catch(() => ({ data: [] })),
    ])
      .then(([p, mc]) => {
        setPoints(p.data || []);
        setMaxicontainers(mc.data || []);
      })
      .catch((e) => {
        console.error(e);
        toast.error("Kunne ikke hente punkter");
      })
      .finally(() => setLoading(false));
  }, [eventId, token]);

  // Saml QR-emner ud fra valg
  const qrItems = useMemo(() => {
    const items = [];

    points.forEach((p) => {
      if (!p.qr_ref) return;
      const isIsland = p.point_type === "ressourceø";
      const isBin = p.point_type === "ressourcespand";
      if (isIsland && includeIslands) items.push({ ...p, kind: "point" });
      else if (isBin && includeBins) items.push({ ...p, kind: "point" });
    });

    if (includeMaxis) {
      maxicontainers.forEach((m) => {
        if (!m.qr_ref) return;
        items.push({
          id: m.id,
          name: m.name,
          qr_ref: m.qr_ref,
          point_type: "maxicontainer",
          kind: "maxi",
        });
      });
    }

    // Sortér: 1) gruppe (ressourceøer først, så spande, så maxicontainere)
    //         2) alfabetisk efter NAVN inden for hver gruppe (dansk: bogstaver før tal, Æ Ø Å til sidst,
    //            og naturlig tal-sortering inden i navne så M1 < M2 < M10)
    const TYPE_ORDER = { 'ressourceø': 0, 'ressourcespand': 1, 'maxicontainer': 2 };
    items.sort((a, b) => {
      const ta = TYPE_ORDER[a.point_type] ?? 99;
      const tb = TYPE_ORDER[b.point_type] ?? 99;
      if (ta !== tb) return ta - tb;
      return (a.name || '').localeCompare(b.name || '', 'da', { numeric: true, sensitivity: 'base' });
    });

    return items;
  }, [points, maxicontainers, includeIslands, includeBins, includeMaxis]);

  // Når listen ændres (typefiltre togles), så vælg automatisk alt der nu er synligt
  useEffect(() => {
    setSelectedRefs(new Set(qrItems.map(it => it.qr_ref)));
  }, [qrItems]);

  const toggleRef = (ref) => {
    setSelectedRefs(prev => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref); else next.add(ref);
      return next;
    });
  };
  const selectAll = () => setSelectedRefs(new Set(qrItems.map(it => it.qr_ref)));
  const selectNone = () => setSelectedRefs(new Set());

  // Generér QR-koder for alle viste emner
  useEffect(() => {
    let cancelled = false;
    if (qrItems.length === 0) return;

    (async () => {
      const newMap = { ...qrMap };
      let added = 0;
      for (const item of qrItems) {
        if (newMap[item.qr_ref]) continue;
        const url = `${window.location.origin}/scan/q/${encodeURIComponent(item.qr_ref)}`;
        try {
          const dataUrl = await QRCode.toDataURL(url, {
            width: 600,
            margin: 1,
            errorCorrectionLevel: "M",
          });
          newMap[item.qr_ref] = dataUrl;
          added++;
        } catch (e) {
          // skip
        }
      }
      if (!cancelled && added > 0) setQrMap(newMap);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrItems]);

  const handlePrint = () => {
    window.print();
  };

  const labelForType = (t) => {
    if (t === "ressourceø") return "Ressourceø";
    if (t === "ressourcespand") return "Skraldespand";
    if (t === "maxicontainer") return "Maxicontainer";
    return t;
  };

  // Vælg en navne-fontstørrelse baseret på længden, så lange navne ikke skubber QR ud af kortet.
  // Højden af navne-området er låst (max-height i CSS), og fonten skaleres så den passer ind.
  const getNameFontSize = (name) => {
    const len = (name || '').length;
    if (len <= 6) return '22mm';
    if (len <= 10) return '16mm';
    if (len <= 14) return '12mm';
    if (len <= 20) return '9mm';
    if (len <= 28) return '7mm';
    return '5.5mm';
  };

  if (!eventId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="text-center">
          <p className="mb-4">Intet event valgt.</p>
          <Button onClick={() => navigate("/")}>Tilbage</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100 print:bg-white text-zinc-900" data-testid="print-all-qr-page">
      {/* Toolbar — skjules ved print */}
      <div className="no-print sticky top-0 z-30 bg-white border-b border-zinc-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            data-testid="print-all-qr-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Tilbage
          </Button>
          <div className="flex-1 min-w-[200px]">
            <h1 className="text-base sm:text-lg font-bold leading-tight">Alle QR-koder</h1>
            <p className="text-xs text-zinc-500">
              {eventName} · {qrItems.length} kode{qrItems.length === 1 ? "" : "r"}
            </p>
          </div>

          {/* Filtre */}
          <div className="flex items-center gap-3 text-xs">
            <label className="flex items-center gap-1 cursor-pointer" data-testid="filter-islands">
              <input
                type="checkbox"
                checked={includeIslands}
                onChange={(e) => setIncludeIslands(e.target.checked)}
              />
              Ressourceøer
            </label>
            <label className="flex items-center gap-1 cursor-pointer" data-testid="filter-bins">
              <input
                type="checkbox"
                checked={includeBins}
                onChange={(e) => setIncludeBins(e.target.checked)}
              />
              Skraldespande
            </label>
            <label className="flex items-center gap-1 cursor-pointer" data-testid="filter-maxis">
              <input
                type="checkbox"
                checked={includeMaxis}
                onChange={(e) => setIncludeMaxis(e.target.checked)}
              />
              Maxicontainere
            </label>
          </div>

          <Button
            onClick={handlePrint}
            disabled={loading || selectedRefs.size === 0}
            data-testid="print-all-qr-btn"
            className="bg-black text-white hover:bg-zinc-800"
          >
            <Printer className="h-4 w-4 mr-1" />
            Print valgte ({selectedRefs.size})
          </Button>
        </div>
        <div className="max-w-6xl mx-auto px-4 pb-3 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-zinc-500">
            Tip: Vælg "Gem som PDF" i print-dialogen. Hver QR udskrives på 15,2 × 15,2 cm med 2 mm bleed og ægte vektor-skærelinje ved 14,8 × 14,8 cm — klar til trykkeriet.
          </p>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-zinc-500">{selectedRefs.size} af {qrItems.length} valgt</span>
            <Button size="sm" variant="outline" onClick={selectAll} data-testid="select-all-btn" className="h-7 text-xs">
              Vælg alle
            </Button>
            <Button size="sm" variant="outline" onClick={selectNone} data-testid="select-none-btn" className="h-7 text-xs">
              Fravælg alle
            </Button>
          </div>
        </div>
      </div>

      {/* Indhold */}
      <div className="max-w-6xl mx-auto py-6 print:py-0 print:max-w-none">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Henter punkter…</span>
          </div>
        ) : qrItems.length === 0 ? (
          <div className="text-center p-12 text-zinc-500">
            Ingen QR-koder fundet for de valgte filtre. Tjek at punkter har et qr_ref tildelt
            (Indstillinger → QR-præfix).
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 px-4 print:block print:gap-0 print:px-0">
            {qrItems.map((item) => {
              const dataUrl = qrMap[item.qr_ref];
              const isSelected = selectedRefs.has(item.qr_ref);
              return (
                <div
                  key={`${item.kind}-${item.id}`}
                  className={`qr-card-wrap relative ${isSelected ? '' : 'qr-card-unselected no-print'}`}
                >
                  {/* Vælg-checkbox — vises kun på skærmen, ikke på print */}
                  <label
                    className="no-print absolute top-2 left-2 z-10 flex items-center gap-1.5 bg-white/90 rounded px-2 py-1 shadow border border-zinc-300 cursor-pointer text-xs font-mono"
                    data-testid={`qr-select-${item.qr_ref}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleRef(item.qr_ref)}
                      className="cursor-pointer"
                    />
                    <span>{item.qr_ref}</span>
                  </label>

                  <div
                    className="qr-card bg-white mx-auto"
                    data-testid={`qr-card-${item.qr_ref}`}
                  >
                    {/* Skæreramme 14,8 × 14,8 cm — ÆGTE SVG-vektor hairline (0,1 mm sort streg).
                        Dokument: 15,2 × 15,2 cm med 2 mm bleed hele vejen rundt. Slutformat efter
                        skæring: 14,8 × 14,8 cm. Trykkeriet kan tildele stregen til en CutContour /
                        Die-spotfarve i Illustrator. */}
                    <svg
                      className="qr-cutframe-svg"
                      width="152mm" height="152mm"
                      viewBox="0 0 152 152"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <rect x="2" y="2" width="148" height="148"
                            fill="none" stroke="#000" strokeWidth="0.1"
                            vectorEffect="non-scaling-stroke" />
                    </svg>
                    <div className="qr-top">
                      <h2 className="qr-name" style={{ fontSize: getNameFontSize(item.name) }}>{item.name}</h2>
                      <p className="qr-type">{labelForType(item.point_type)}</p>
                    </div>
                    <div className="qr-img-wrap">
                      {dataUrl ? (
                        <img src={dataUrl} alt={`QR ${item.qr_ref}`} className="qr-img" />
                      ) : (
                        <div className="qr-placeholder">
                          <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                      )}
                    </div>
                    <p className="qr-cta">Scan for tømning</p>
                    <div className="qr-footer">
                      <span className="qr-ref">{item.qr_ref}</span>
                      <div className="qr-reuse">Genbrugelig QR — samme sticker virker år efter år</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        /* Wrap for hver QR — bruges til at huse vælge-checkbox + dimmed overlay */
        .qr-card-wrap { display: block; }
        .qr-card-unselected .qr-card {
          opacity: 0.35;
          filter: grayscale(0.6);
          transition: opacity 0.15s ease;
        }
        .qr-card {
          width: 152mm;
          height: 152mm;
          padding: 10mm 12mm;  /* 2 mm bleed + 8/10 mm indrykning, så indhold ligger inden for skæreramme */
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          font-family: 'Arial Black', Arial, sans-serif;
          box-sizing: border-box;
          position: relative;
        }
        /* SVG-cut-linje ligger absolut bag indholdet og fylder hele kortet */
        .qr-cutframe-svg {
          position: absolute;
          top: 0;
          left: 0;
          width: 152mm;
          height: 152mm;
          pointer-events: none;
        }
        .qr-top {
          width: 100%;
          text-align: center;
          /* Fast højde, så QR-position er låst uanset navne-længde */
          height: 35mm;
          max-height: 35mm;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .qr-name {
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 1mm;
          line-height: 1;
          margin: 0;
          word-break: break-word;
          /* font-size sættes dynamisk via inline style baseret på navne-længde */
        }
        .qr-type {
          font-size: 4mm;
          color: #555;
          letter-spacing: 1mm;
          margin: 1mm 0 0 0;
          font-weight: normal;
          font-family: Arial, sans-serif;
          text-transform: capitalize;
        }
        .qr-img-wrap {
          flex: 1 1 auto;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          margin: 2mm 0;
        }
        .qr-img { width: 75mm; height: 75mm; display: block; }
        .qr-placeholder {
          width: 75mm; height: 75mm;
          display: flex; align-items: center; justify-content: center;
          background: #f4f4f4;
        }
        .qr-cta {
          font-size: 9mm;
          font-weight: 900;
          color: #000;
          margin: 0;
          letter-spacing: 0.5mm;
          text-transform: uppercase;
          text-align: center;
          line-height: 1;
        }
        .qr-footer {
          margin-top: 2mm;
          padding-top: 1.5mm;
          border-top: 1px solid #aaa;
          width: 100%;
          text-align: center;
          font-family: Arial, sans-serif;
        }
        .qr-ref {
          font-family: 'Courier New', monospace;
          font-size: 4.5mm;
          font-weight: bold;
          color: #333;
          letter-spacing: 1.5mm;
        }
        .qr-reuse {
          font-size: 2.5mm;
          color: #999;
          margin-top: 0.5mm;
          font-style: italic;
          font-weight: normal;
        }

        @media print {
          @page { size: 152mm 152mm; margin: 0; }
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .qr-card {
            border: none !important;
            page-break-after: always;
            margin: 0 !important;
          }
          .qr-card:last-child { page-break-after: auto; }
          /* SVG-cutlinjen er ægte vektor og bevares automatisk i PDF */
        }
      `}</style>
    </div>
  );
}
